// ToyCard.tsx
import React, { useState, useEffect, memo } from "react";
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  Fade,
} from "@mui/material";
import Skeleton from "@mui/material/Skeleton";
import { Product } from "../../redux/types/productTypes";
import ReactRouterLink from "../common/ReactRouterLink";

interface ToyCardProps {
  product: Product;
  loading?: boolean;
}

const ToyCard: React.FC<ToyCardProps> = memo(({ product, loading = false }) => {
  const { productName, productPrice, productImage, productCondition } = product;
  const isNew = productCondition?.toLowerCase() === "new";

  const [imageLoaded, setImageLoaded] = useState(false);
  const [detailsLoaded, setDetailsLoaded] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const previousImageRef = React.useRef<string>("");

  useEffect(() => {
    // Reduce delay to prevent blinking - show content faster
    const timeout = setTimeout(() => setDetailsLoaded(true), 100);
    return () => clearTimeout(timeout);
  }, []);

  useEffect(() => {
    // Only reload image if the image URL actually changed
    if (productImage !== previousImageRef.current) {
      previousImageRef.current = productImage;
      setImageLoaded(false);
      const img = new Image();
      img.src = productImage;
      img.onload = () => setImageLoaded(true);
      img.onerror = () => setImageLoaded(false);
    }
  }, [productImage]);

  return (
    <ReactRouterLink
      to={product.productUrl}
      isLinkUnderline={false}
      isDisabled={loading || !detailsLoaded}
      linkElement={
        <Card
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          sx={{
            position: "relative",
            borderRadius: 3,
            cursor: "pointer",
            overflow: "hidden",
            background: "white",
            transition: "0.25s ease-in-out",
            transform: isHovered ? "translateY(-6px) scale(1.02)" : "none",
            boxShadow: isHovered
              ? "0px 10px 25px rgba(0,0,0,0.18)"
              : "0px 3px 10px rgba(0,0,0,0.06)",
          }}
        >
          {/* Image */}
          <Box
            sx={{
              position: "relative",
              height: 210,
              background: "linear-gradient(180deg, #f8f8f8, #ececec)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              p: 1,
            }}
          >
            {!imageLoaded && (
              <Skeleton
                variant="rectangular"
                height="100%"
                width="100%"
                animation="wave"
              />
            )}

            {imageLoaded && (
              <Fade in={true} timeout={500}>
                <img
                  src={productImage}
                  alt={productName}
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                  }}
                />
              </Fade>
            )}

            {/* Condition + Price */}
            {detailsLoaded && (
              <Box
                sx={{
                  position: "absolute",
                  bottom: 0,
                  width: "100%",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  px: 1,
                  py: 0.5,
                  background: "rgba(0,0,0,0.55)",
                  backdropFilter: "blur(6px)",
                }}
              >
                <Chip
                  label={isNew ? "New" : "Used"}
                  size="small"
                  sx={{
                    bgcolor: isNew ? "#4caf50" : "#6d6d6d",
                    color: "white",
                    fontSize: "0.65rem",
                    height: 18,
                  }}
                />
                <Typography
                  sx={{
                    color: "#fff",
                    fontWeight: 700,
                    fontSize: 15,
                  }}
                >
                  {productPrice}
                </Typography>
              </Box>
            )}
          </Box>

          {/* Title */}
          <CardContent sx={{ p: 1.5 }}>
            {!detailsLoaded ? (
              <Skeleton variant="text" width="80%" sx={{ mx: "auto" }} />
            ) : (
              <Typography
                sx={{
                  color: "#444",
                  fontSize: 13.5,
                  fontWeight: 500,
                  textAlign: "center",
                  lineHeight: "1.25em",
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                  minHeight: "2.5em",
                }}
              >
                {productName}
              </Typography>
            )}
          </CardContent>
        </Card>
      }
    />
  );
}, (prevProps, nextProps) => {
  // Return true if props are equal (skip re-render), false if different (re-render)
  // Only re-render if product data actually changed OR loading state changed from false to true
  const productChanged = 
    prevProps.product.documentId !== nextProps.product.documentId ||
    prevProps.product.productImage !== nextProps.product.productImage ||
    prevProps.product.productName !== nextProps.product.productName ||
    prevProps.product.productPrice !== nextProps.product.productPrice ||
    prevProps.product.productCondition !== nextProps.product.productCondition;
  
  // Only re-render if product changed, or if loading goes from false to true (not true to false)
  const loadingChanged = prevProps.loading !== nextProps.loading;
  const shouldRerender = productChanged || (loadingChanged && nextProps.loading);
  
  return !shouldRerender; // Return true to skip re-render, false to re-render
});

export default ToyCard;
